/**
 * Promo band behaviour: the media carousel, video slides, and the body's
 * scroll solidify.
 *
 * Scroll container: Horizon's squeeze layout makes `.page-wrapper` the page
 * scroller at >=990px (base.css puts html + body at height:100dvh /
 * overflow:hidden there), and `document.scrollingElement` below it. An element
 * scroll event does not bubble, so a document-level listener is deaf on
 * desktop. Every scrollport read below goes through @theme/scroll-container,
 * the same helper header.js uses, and the bindings are rebuilt when the
 * viewport crosses that breakpoint. The carousel's own listener is on the
 * track element, which emits its own scroll events, so it stays as it is.
 */

import {
  getScrollContainer,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * Promo band image carousel.
 *
 * The track is a native scroll-snap region, so swiping works with zero JS.
 * This element only keeps the dots in sync with the scroll position and lets
 * a dot click scroll to its slide.
 */
class LfMediaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lf-track]');
    this.dots = Array.from(this.querySelectorAll('[data-lf-dot]'));

    if (!this.track || this.dots.length < 2) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.setActive(0);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.handleScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  handleScroll() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const width = this.track.clientWidth;
      if (!width) return;
      this.setActive(Math.round(Math.abs(this.track.scrollLeft) / width));
    });
  }

  goTo(index) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollTo({
      left: index * this.track.clientWidth,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.setActive(index);
  }

  setActive(index) {
    const clamped = Math.max(0, Math.min(index, this.dots.length - 1));

    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === clamped ? 'true' : 'false');
    });
  }
}

if (!customElements.get('lf-media-carousel')) {
  customElements.define('lf-media-carousel', LfMediaCarousel);
}

/**
 * A video slide.
 *
 * The <video> is rendered by video_tag and already sits in the image's exact
 * box, so this element only decides who starts it.
 *
 *   click    the markup ships poster + controls, this lays a cover still and a
 *            play pill over it, and the first click unmutes and plays. Controls
 *            stay on underneath: if the browser's autoplay policy refuses an
 *            unmuted play() there is still something to press.
 *   ambient  the markup autoplays it muted on a loop with no chrome, so there
 *            is nothing to do — unless the visitor has asked for reduced
 *            motion, where the loop is stopped and the mode degrades to click
 *            (muted, since an ambient clip was authored without sound).
 *
 * Cover visibility is one attribute on the host (see the CSS). It is only ever
 * written from here, so a page without JS shows no cover at all and falls back
 * to the native player rather than to a dead button.
 */
class LfVideoMedia extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.cover = this.querySelector('[data-lf-cover]');

    if (!this.video || !this.cover) return;

    this.ambient = this.dataset.lfMode === 'ambient';

    if (this.ambient) {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // The attribute is cleared as well as paused, or a later load()/source
      // change would start it again behind the cover.
      this.video.autoplay = false;
      this.video.removeAttribute('autoplay');
      this.video.pause();
    }

    this.handleActivate = this.handleActivate.bind(this);
    this.cover.addEventListener('click', this.handleActivate);
    this.dataset.lfCoverState = 'visible';
  }

  disconnectedCallback() {
    if (this.handleActivate) this.cover?.removeEventListener('click', this.handleActivate);
  }

  /** One handler for the whole cover: the play pill's click bubbles into it. */
  handleActivate() {
    this.video.controls = true;
    if (!this.ambient) this.video.muted = false;

    this.dataset.lfCoverState = 'hidden';

    // A rejected play() is not an error here — the controls are showing, so the
    // visitor simply presses the native button instead.
    const played = this.video.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});

    this.video.focus({ preventScroll: true });
  }
}

if (!customElements.get('lf-video-media')) {
  customElements.define('lf-video-media', LfVideoMedia);
}

/** Bodies at or under this many words lift gently instead of sweeping. */
const SUBTLE_WORD_COUNT = 32;

/**
 * Scroll-driven text solidify.
 *
 * The body starts in the band's faded tint and fills word by word as the
 * section climbs the viewport — the design draws the mid-scroll state,
 * first paragraph solid, second still sage.
 *
 * The split walks text nodes only and never touches innerHTML, so whatever the
 * editor's rich text contains survives intact: paragraphs, lists, <strong>,
 * <em>, and links, which stay clickable because the words are wrapped inside
 * the anchor rather than around it. Each word takes its solid end from
 * currentColor, so a link keeps its own colour through the sweep.
 *
 * Per frame this writes a single custom property on the element; the per-word
 * maths lives in CSS (see lf-promo-band.css "Scroll solidify").
 */
class LfTextSolidify extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!this.words) {
      // Assigned before the length guard so a re-connect never re-walks a body
      // that has already been split.
      this.words = this.splitWords();
      this.handleScroll = this.handleScroll.bind(this);
      this.rebind = this.rebind.bind(this);
      this.stampWords();
    }

    if (this.words.length < 2) return;

    // Crossing 990px swaps the page scroller, so the listener target and the
    // observer root both go stale. Rebuild them.
    scrollContainerMediaQuery.addEventListener('change', this.rebind);

    this.observe();
    this.update();
  }

  disconnectedCallback() {
    this.setListening(false);
    this.observer?.disconnect();
    this.observer = null;
    if (this.rebind) scrollContainerMediaQuery.removeEventListener('change', this.rebind);
  }

  /** Each word's own place in the sweep, written once at split time. */
  stampWords() {
    if (this.words.length < 2) return;

    // A word fills over 1/gain of the sweep, so the last word has to start its
    // fill that far before the end for the sweep to finish exactly at 1.
    const gain = parseFloat(getComputedStyle(this).getPropertyValue('--lf-pb-word-gain')) || 5.5;
    const spread = 1 - 1 / gain;

    this.words.forEach((word, index) => {
      word.style.setProperty('--lf-w', ((index / (this.words.length - 1)) * spread).toFixed(4));
    });

    if (this.words.length <= SUBTLE_WORD_COUNT) this.dataset.lfSweep = 'subtle';
  }

  /**
   * Only run the per-frame work while the band is anywhere near the scrollport
   * — nine of these render across the site. The root has to be the page
   * scroller, or the outward rootMargin is clipped away on desktop.
   */
  observe() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => this.setListening(entry.isIntersecting)),
      { root: getIntersectionRoot(), rootMargin: '25% 0px' }
    );
    this.observer.observe(this);
  }

  /** The page scroller changed under us: re-point the observer and the listener. */
  rebind() {
    const wasListening = this.listening;
    this.setListening(false);
    this.observe();
    if (wasListening) this.setListening(true);
  }

  splitWords() {
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;

    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim()) textNodes.push(node);
    }

    const words = [];

    textNodes.forEach((textNode) => {
      const fragment = document.createDocumentFragment();

      // Whitespace stays a plain text node so wrapping and spacing are exactly
      // what they were before the split.
      textNode.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part) return;

        if (!part.trim()) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }

        const word = document.createElement('span');
        word.className = 'lf-promo-band__word';
        word.textContent = part;
        fragment.appendChild(word);
        words.push(word);
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    });

    return words;
  }

  setListening(listening) {
    if (listening === this.listening) return;
    this.listening = listening;

    if (listening) {
      // Held so the detach hits the same target even if the breakpoint has
      // moved in between.
      this.scrollTarget = getScrollEventTarget();
      this.scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
      this.update();
      return;
    }

    this.scrollTarget?.removeEventListener('scroll', this.handleScroll);
    this.scrollTarget = null;
    window.removeEventListener('resize', this.handleScroll);

    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  handleScroll() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  /**
   * The box the sweep is measured against: the page scroller's own scrollport,
   * not the window. The two match on mobile; on desktop the scroller is
   * `.page-wrapper`.
   */
  scrollport() {
    const container = getScrollContainer();
    const isRoot = container === document.scrollingElement || container === document.documentElement;

    return {
      top: isRoot ? 0 : container.getBoundingClientRect().top,
      height: container.clientHeight || window.innerHeight,
    };
  }

  update() {
    const port = this.scrollport();
    const rect = this.getBoundingClientRect();
    // Distance from the scrollport's top edge, which is what the sweep window
    // below is measured against.
    const top = rect.top - port.top;

    // Zero as the body's top crosses 80% of the scrollport, one once its bottom
    // has climbed to 60% — the sweep spans the block's own height plus a fifth
    // of a screen, so long and short bodies both fill at a readable pace.
    const travel = Math.max(rect.height + port.height * 0.2, port.height * 0.2);
    const progress = (port.height * 0.8 - top) / travel;

    this.style.setProperty('--lf-p', Math.min(1, Math.max(0, progress)).toFixed(4));
  }
}

if (!customElements.get('lf-text-solidify')) {
  customElements.define('lf-text-solidify', LfTextSolidify);
}
